/**
 * Full-screen branded "checking session" splash.
 *
 * Shown by the root route while the admin auth provider restores a session
 * after a page reload (real mode). Uses the shared Zone wordmark + the
 * brand-orange spinner ring on the warm-neutral dark surface.
 *
 * This is intentionally separate from `LoadingState` (the small inline /
 * card loader used inside data tables) because this is a page-level gate
 * with its own composition and motion.
 */

import { ZoneWordmark } from "@/components/common/ZoneWordmark";

export interface SessionLoadingScreenProps {
  label?: string;
}

export function SessionLoadingScreen({
  label = "Checking session",
}: SessionLoadingScreenProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="session-splash flex min-h-screen flex-col items-center justify-center gap-7 bg-[var(--bg)] px-6 text-center"
    >
      {/* Brand-orange spinner ring with the pulsing logo dot at its center */}
      <div className="relative flex h-16 w-16 items-center justify-center">
        <span
          aria-hidden
          className="session-splash__ring absolute inset-0 rounded-full"
        />
        <span
          aria-hidden
          className="session-splash__dot h-3 w-3 rounded-full bg-[var(--orange)]"
        />
      </div>

      {/* Wordmark + console tag */}
      <div className="flex flex-col items-center gap-2">
        <ZoneWordmark height={28} title="Zone" />
        <div className="mono text-text-mute">Admin Console</div>
      </div>

      {/* Status line with pulsing dots */}
      <div className="flex items-center gap-1.5 text-sm text-text-mute">
        <span>{label}</span>
        <span aria-hidden className="session-splash__dots flex items-end gap-1">
          <i />
          <i />
          <i />
        </span>
      </div>
    </div>
  );
}
